import { SCHEMA } from '@vedalib/editor/lib/brand'
import React, { useCallback, useMemo } from 'react'

import Padding from 'components/Editor/Toolbar/Padding'
import { LayoutScroll } from 'components/LayoutPage'
import {
  FieldColorPicker,
  FieldNumberArray,
  FieldSegmented,
  FieldSlider,
  FormField,
} from 'components/formKit'
import { TileButton } from 'components/uiKit/Button'
import { Typography } from 'components/uiKit/Typography'
import { BrandFieldShadow, BrandTagEdited } from 'services/Branding/components'
import { BORDER_OPTIONS, OFFSET_ARRAY } from 'services/Branding/constants'
import {
  useBrandColorOptions,
  useShowBorder,
  useThemeValueGetter,
} from 'services/Branding/fieldConfigs/useFieldConfigs'
import { isAnyEdited } from 'services/Branding/fieldConfigs/utils'
import { genSchema, genBrandField } from 'services/Branding/genSchema'
import { useUpdateSchema } from 'services/Branding/hooks'
import { BrandFormEnum } from 'services/Branding/types'
import { t } from 'services/Translation'

import type { TestKeyboardInputFormBrandType } from './testKeyboardInputTypes'

export const STYLE_FORM_SCHEMA = genSchema(SCHEMA.testKeyboardInput.style, {
  input: {
    backgroundColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('input.label.backgroundColor'),
      props: { transparent: true, labeled: true },
      useConfigs: [useBrandColorOptions],
    }),
    borderRadius: genBrandField({
      as: FieldSlider,
      label: t('input.label.borderRadius'),
      props: { min: 0, max: 50, showInput: true },
      useConfigs: [useThemeValueGetter],
    }),
    borderStyle: genBrandField({
      as: FieldSegmented<'none' | 'solid'>,
      label: t('input.label.border'),
      props: { options: BORDER_OPTIONS },
      useConfigs: [useThemeValueGetter],
    }),
    borderColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('input.label.borderColor'),
      useConfigs: [useBrandColorOptions, useShowBorder],
    }),
    borderWidth: genBrandField({
      as: FieldSlider,
      label: t('input.label.borderWidth'),
      props: { min: 1, max: 8, showInput: true },
      useConfigs: [useShowBorder],
    }),
    padding: genBrandField({
      as: FieldNumberArray,
      label: t('elements.testKeyboardInput.schema.padding'),
      layout: 'vertical',
      props: { options: OFFSET_ARRAY },
    }),
  },
  input_hover: {
    backgroundColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('style.hoverColor'),
      props: { transparent: true, labeled: true, resettable: true },
      useConfigs: [useBrandColorOptions],
    }),
  },
  input_focus: {
    backgroundColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('style.focusColor'),
      props: { transparent: true, labeled: true, resettable: true },
      useConfigs: [useBrandColorOptions],
    }),
  },
  effects: {
    boxShadow: genBrandField({
      as: BrandFieldShadow,
      label: t('elements.testKeyboardInput.schema.boxShadow'),
      useConfigs: [useThemeValueGetter],
    }),
  },
})

const TestKeyboardInputFormStyle: TestKeyboardInputFormBrandType = ({
  device,
  subConfigs,
  setForm,
  edited,
}) => {
  const path = useMemo(() => ['style', device], [device])
  const schema = useUpdateSchema(path, subConfigs, STYLE_FORM_SCHEMA)

  const handleSetTypography = useCallback(() => setForm?.(BrandFormEnum.TYPOGRAPHY), [setForm])

  const handleSetFeedback = useCallback(() => setForm?.(BrandFormEnum.FEEDBACK), [setForm])

  return (
    <LayoutScroll sizeAutoCapable>
      <Padding bottom gap sides>
        <FormField config={schema.input.backgroundColor} />
        <FormField config={schema.input.borderRadius} />
        <FormField config={schema.input.borderStyle} />
        <FormField config={schema.input.borderColor} />
        <FormField config={schema.input.borderWidth} />
        <FormField config={schema.input.padding} />
        <FormField config={schema.input_hover.backgroundColor} />
        <FormField config={schema.input_focus.backgroundColor} />

        <Typography weight='medium'>{t('style.effects')}</Typography>
        <FormField config={schema.effects.boxShadow} />

        <TileButton
          description={t('uiKit.button.feedbackDescription')}
          icon='otherRight'
          name='showFeedbackForm'
          onClick={handleSetFeedback}
        >
          <Typography weight='medium'>{t('page.brand.tab.feedback')}</Typography>
          {isAnyEdited(edited.feedback) && <BrandTagEdited />}
        </TileButton>

        <TileButton
          description={t('uiKit.button.richTextDescription')}
          icon='otherRight'
          name='showFontForm'
          onClick={handleSetTypography}
        >
          <Typography weight='medium'>{t('page.brand.tab.typography')}</Typography>
          {isAnyEdited(edited.font) && <BrandTagEdited />}
        </TileButton>
      </Padding>
    </LayoutScroll>
  )
}

export default React.memo(TestKeyboardInputFormStyle) as typeof TestKeyboardInputFormStyle
